import { useCallback, useMemo, useState, forwardRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation, Trans } from 'react-i18next';
import { Box, Stack, Tooltip } from '@mui/material';
import { AutoAwesomeOutlined, InfoOutlined } from '@mui/icons-material';
import { Button, Typography } from 'melp-design/components';
import { mergeRefs } from 'utils/general';
import { formatBytes, formatList } from 'utils/format';
import { SystemColors } from 'melp-design/style';
import { useModalContext } from 'store/modal';
import { AiGenerationModal, FilesList } from './atoms';
import { DragState, Props } from './file-input.types';
import { MIME_TYPE_BY_FILE } from './file-input.constants';

export const FileInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      accept,
      maxSize = 25 * 1024 * 1024,
      maxFiles = 2,
      name,
      disabled,
      required,
      onChange,
      variant = 'comfort',
      error,
      previewSelectedFiles = true,
      ai,
    },
    forwardedRef,
  ) => {
    const { t, i18n } = useTranslation();
    const { openModal } = useModalContext();

    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    const handleChange = (files: File[]) => {
      setUploadedFiles(files);
      onChange?.(files);
    };

    const onDrop = useCallback<typeof handleChange>(handleChange, [
      handleChange,
    ]);

    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragAccept,
      isDragReject,
      open,
      inputRef,
    } = useDropzone({
      accept: accept?.reduce(
        (acc, filetype) => ({
          ...acc,
          [MIME_TYPE_BY_FILE[filetype]]: [`.${filetype}`],
        }),
        {},
      ),
      onDrop,
      disabled,
      maxSize,
      maxFiles,
      noClick: true,
      noKeyboard: true,
    });

    const dragState = useMemo<DragState>(() => {
      return isDragAccept
        ? 'accept'
        : isDragReject
        ? 'reject'
        : isDragActive
        ? 'active'
        : 'inactive';
    }, [isDragAccept, isDragReject, isDragActive]);

    const info = (
      <Box>
        {!!accept?.length && (
          <Typography variant="p1">
            <Trans
              i18nKey="components.file_input.supported"
              values={{
                filetypes: formatList(
                  i18n.language,
                  accept.map((filetype) => `.${filetype}`),
                ),
              }}
              components={[
                <Box component="span" sx={{ fontWeight: 500 }} key="0" />,
              ]}
            />
          </Typography>
        )}
        <Typography variant="p1">
          <Trans
            i18nKey="components.file_input.max"
            values={{
              maxSize: formatBytes(maxSize),
            }}
            components={[
              <Box component="span" sx={{ fontWeight: 500 }} key="0" />,
            ]}
          />
        </Typography>
      </Box>
    );

    const showSelectedFiles = !!uploadedFiles.length && previewSelectedFiles;

    return (
      <Stack sx={{ fontSize: 16 }} gap={1}>
        {label ? (
          <Typography variant="p1" color="textSecondary">
            {label}
          </Typography>
        ) : null}

        {showSelectedFiles ? (
          <FilesList
            files={uploadedFiles}
            onRemove={(name) =>
              handleChange(
                uploadedFiles.filter((uploaded) => uploaded.name !== name),
              )
            }
          />
        ) : (
          <Box
            {...getRootProps({
              disabled,
              'data-drag-state': dragState,
            })}
            px={1.5}
            py={variant === 'comfort' ? 1.5 : 1}
            sx={{
              width: '100%',
              background: SystemColors.white,
              borderWidth: 1,
              borderStyle: variant === 'comfort' ? 'dashed' : 'solid',
              borderColor: error
                ? SystemColors.danger.base
                : variant === 'comfort'
                ? SystemColors.grey[55]
                : SystemColors.grey[90],
              borderRadius: 1,
              "&[data-drag-state='accept'],&[data-drag-state='active']": {
                borderColor: SystemColors.success.base,
                background: SystemColors.grey[96],
              },
              "&[data-drag-state='reject']": {
                background: SystemColors.danger.base,
              },
              '&[disabled]': {
                background: SystemColors.grey[96],
              },
            }}
          >
            <input
              {...getInputProps({
                multiple: maxFiles > 1,
                name,
                disabled,
                required: required ? !uploadedFiles.length : undefined,
              })}
              ref={mergeRefs(inputRef, forwardedRef)}
              // hack to replace `display: none` so input can be focused on required error
              // https://github.com/react-dropzone/react-dropzone/issues/1268
              style={{
                display: 'initial',
                opacity: '0',
                position: 'absolute',
                zIndex: -1,
              }}
            />
            <Stack
              flexDirection={variant === 'comfort' ? 'column' : 'row'}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              sx={{
                textAlign: 'center',
              }}
            >
              <Stack
                flexDirection="row"
                gap={1}
                color={
                  variant === 'comfort'
                    ? SystemColors.black
                    : SystemColors.grey[55]
                }
              >
                {variant === 'compact' ? (
                  <Tooltip title={info}>
                    <InfoOutlined fontSize="small" color="inherit" />
                  </Tooltip>
                ) : null}

                <Typography variant="h3" color="inherit" noWrap>
                  {t('components.file_input.drag_n_drop')}
                </Typography>
              </Stack>

              <Stack gap={1} flexDirection="row">
                {ai ? (
                  <Button
                    label={t('components.file_input.generate_ai')}
                    onClick={async () => {
                      const response = await openModal({
                        component: AiGenerationModal,
                        props: {
                          title: ai.title,
                          description: ai.description,
                        },
                        size: 'xl',
                      });

                      if (response.action === 'SELECT') {
                        handleChange([response.file]);
                      }
                    }}
                    size="sm"
                    variant={
                      variant === 'comfort' ? 'secondary' : 'neutral-fill'
                    }
                    disabled={disabled}
                    icon={AutoAwesomeOutlined}
                  />
                ) : null}

                <Button
                  label={t('components.file_input.browse')}
                  variant={
                    variant === 'comfort' ? 'primary' : 'neutral-outline'
                  }
                  onClick={open}
                  size="sm"
                  disabled={disabled}
                />
              </Stack>

              {variant === 'comfort' ? info : null}
            </Stack>
          </Box>
        )}

        {error ? (
          <Typography variant="p1" color="error">
            {error}
          </Typography>
        ) : null}
      </Stack>
    );
  },
);

FileInput.displayName = 'FileInput';
